import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedProps, useAnimatedStyle } from 'react-native-reanimated';
import { rem } from 'design-system/values';
import { Spacer } from 'design-system/Spacer';
import Text, { styles as textStyles } from 'design-system/Text';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { getColorKeyFromPriceTrendWorklet } from '../utils/portfolio';
// The tooltip's free side. 'auto' on every platform: Reanimated 4 keeps a
// value it set earlier when the next style leaves it undefined, so the box
// stayed pinned on both sides after the cursor crossed the middle.
const defaultLeftAndRight = 'auto';
const SelectedCandleInfoView = ({ totalCandles, eachCandleWidth, selectedInfoObj, }) => {
    const colors = useColors();
    const styles = useStyles(styleSheet);
    const animatedStyles = useAnimatedStyle(() => {
        if (!totalCandles || !selectedInfoObj.value) {
            return {
                left: -500,
                right: defaultLeftAndRight,
            };
        }
        const { originalX } = selectedInfoObj.value;
        if (originalX > 0.6 * totalCandles) {
            return {
                left: defaultLeftAndRight,
                right: eachCandleWidth * (totalCandles - originalX + 1),
            };
        }
        return {
            right: defaultLeftAndRight,
            left: eachCandleWidth * (originalX + 2),
        };
    }, [eachCandleWidth, selectedInfoObj, totalCandles]);
    const openPriceProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value ? selectedInfoObj.value.priceOpen : '',
    }), [selectedInfoObj]);
    const closePriceProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value ? selectedInfoObj.value.priceClose : '',
    }), [selectedInfoObj]);
    const highPriceProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value ? selectedInfoObj.value.priceHigh : '',
    }), [selectedInfoObj]);
    const lowPriceProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value ? selectedInfoObj.value.priceLow : '',
    }), [selectedInfoObj]);
    const changePercentProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value ? selectedInfoObj.value.priceChangePct : '',
        color: colors.text[getColorKeyFromPriceTrendWorklet(selectedInfoObj.value?.priceTrend)],
    }), [colors.text, selectedInfoObj]);
    return (<Animated.View style={styles.container} pointerEvents="none">
      <Animated.View style={[styles.flex, animatedStyles]}>
        <View style={styles.infoContainer}>
          <View>
            <Text TextThin-12 Secondary>
              Open:
            </Text>
            <Spacer h={6}/>
            <Text TextThin-12 Secondary>
              Close:
            </Text>
            <Spacer h={6}/>
            <Text TextThin-12 Secondary>
              High:
            </Text>
            <Spacer h={6}/>
            <Text TextThin-12 Secondary>
              Low:
            </Text>
            <Spacer h={6}/>
            <Text TextThin-12 Secondary>
              Change:
            </Text>
          </View>
          <Spacer w={8}/>
          <View style={styles.priceContainer}>
            <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.priceStyles} animatedProps={openPriceProps}/>
            <Spacer h={6}/>
            <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.priceStyles} animatedProps={closePriceProps}/>
            <Spacer h={6}/>
            <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.priceStyles} animatedProps={highPriceProps}/>
            <Spacer h={6}/>
            <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.priceStyles} animatedProps={lowPriceProps}/>
            <Spacer h={6}/>
            <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.priceStyles} animatedProps={changePercentProps}/>
          </View>
        </View>
      </Animated.View>
    </Animated.View>);
};
export default React.memo(SelectedCandleInfoView);
const styleSheet = createStyleSheets((colors) => ({
    container: {
        ...StyleSheet.absoluteFillObject,
        flexDirection: 'row',
    },
    infoContainer: {
        padding: rem(12),
        borderRadius: rem(8),
        flexDirection: 'row',
        backgroundColor: colors.background.light,
        ...colors.shadows.cardLarge,
    },
    priceContainer: {
        alignItems: 'flex-end',
    },
    flex: {
        top: 0,
        bottom: 0,
        position: 'absolute',
        flexDirection: 'row',
        alignItems: 'center',
    },
    priceStyles: {
        ...textStyles.NumbersThin_12,
        margin: 0,
        padding: 0,
        height: rem(18),
        color: colors.text.primary,
    },
}));
